// Import necessary types and hooks
import { WithdrawMethodId, WithdrawStep } from '../../../enums';
import { CoinType } from '../../../models';
import { useWithdrawForm } from '../useWithdrawForm';
import { useWithdrawSearchParams } from '../useWithdrawSearchParams';
import { UseWithdrawFormStateProps } from './props';

/**
 * Custom hook to manage the state of the withdrawal form.
 * This hook determines the validity of the form based on the current step in the withdrawal process.
 *
 * @param {UseWithdrawFormStateProps} props - The properties for the hook.
 * @param {Function} props.watch - Function to watch form fields.
 * @param {Function} props.setValue - Function to set form field values.
 * @returns {boolean} A boolean indicating whether the form is in a valid state for the current step.
 *
 * @example
 * // Usage example:
 * const isFormValid = useWithdrawFormState({
 *   watch: formWatchFunction,
 *   setValue: formSetValueFunction,
 * });
 */
export function useWithdrawFormState({
  watch,
  setValue,
}: UseWithdrawFormStateProps) {
  // Retrieve withdrawal method ID, coin type, and transaction step from search parameters
  const { withdrawMethodId, coinType, transactionStep } =
    useWithdrawSearchParams();

  // Watch specific form fields for changes
  const amount = watch('amount');
  const code: string = watch('crypto.code');
  const emailId: bigint = watch('crypto.emailId');
  const userPaymentDetailSelectedAccount = watch('paymentDetailsId');

  // Destructure necessary values from the useWithdrawForm hook
  const {
    tokenLookupQuery: { tokenLookup, isPendingTokenLookup },
    feeQuery: { isErrorFee, isLoadingFee },
    isOverValued,
    isUnderValued,
    isValidReceiveAmount,
  } = useWithdrawForm(watch, setValue);

  // Determine form validity based on the current transaction step
  switch (transactionStep) {
    case WithdrawStep.Form:
      // Check conditions for the Form step
      return (
        amount === 0 ||
        !withdrawMethodId ||
        isOverValued ||
        isUnderValued ||
        !amount ||
        !isValidReceiveAmount ||
        isLoadingFee ||
        isErrorFee ||
        (coinType == CoinType.Crypto &&
          withdrawMethodId === WithdrawMethodId.Crypto?.toString() &&
          (!tokenLookup?.valid || isPendingTokenLookup))
      );
    case WithdrawStep.UserPaymentDetails:
      // Check conditions for the User Payment Details step
      return (
        (coinType == CoinType.Fiat && !userPaymentDetailSelectedAccount) ||
        isErrorFee
      );
    case WithdrawStep.Confirmation:
      // Check conditions for the Confirmation step
      return !emailId || isErrorFee;
    case WithdrawStep.ConfirmationCode:
      // Check conditions for the Confirmation Code step
      return !code || code?.length < 6 || isErrorFee;
    case WithdrawStep.Response:
      // The Response step is always valid
      return false;
  }
}

// Export the type for useWithdrawFormState props
export type { UseWithdrawFormStateProps };
